import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '../../../components/bootstrap/Button';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import Icon from '../../../components/icon/Icon';
import Badge from '../../../components/bootstrap/Badge';
import Spinner from '../../../components/bootstrap/Spinner';
import { getAPI, postAPI } from '../../../apis/backend/apiNode';
import Textarea from '../../../components/bootstrap/forms/Textarea';
import { formatCurrency } from '../../comptabilite/utils/functions';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import CreationModal from '../../../components/common/CreationModal';
import {
	getProduitCreationFields,
	formatProduitData,
} from '../../../components/common/fieldsConfig';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import { isMobile, isTablet } from 'react-device-detect';

const DevisLigneRow = ({
	ligne = {},
	index,
	onUpdate,
	onRemove,
	loading,
	products = [],
	company,
}) => {
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [stockStatus, setStockStatus] = useState(null);
	const [checkingStock, setCheckingStock] = useState(false);

	// Use react-device-detect for device detection
	const shouldRenderMobileCard = isMobile || isTablet;

	const typeLigneOptions = useMemo(
		() => [
			{ value: 'produit', label: 'Produit' },
			{ value: 'service', label: 'Service' },
			{ value: 'note', label: 'Note' },
		],
		[],
	);

	const searchProduits = useCallback(async (term = '') => {
		if (!term || term.trim() === '') {
			return { success: true, data: [] };
		}

		try {
			const response = await getAPI(
				'stock',
				`/produits/recherche?term=${encodeURIComponent(term)}&limit=10`,
			);

			return {
				...response,
				data: response.data?.produits || [],
			};
		} catch (error) {
			console.error('Erreur recherche produits:', error);
			return { success: false, error: error.message };
		}
	}, []);

	const createProduit = useCallback(async (produitData) => {
		try {
			const formattedData = formatProduitData(produitData);
			const result = await postAPI('stock', formattedData, '/produits');

			if (result?.success) {
				return result.data;
			}
			throw new Error(result?.error || 'Erreur lors de la création');
		} catch (err) {
			console.error('Create Produit error:', err);
			throw err;
		}
	}, []);

	const checkStock = useCallback(
		async (produitId, quantite) => {
			if (!produitId || ligne.type !== 'produit') {
				setStockStatus(null);
				return;
			}

			setCheckingStock(true);
			try {
				const response = await getAPI(
					'stock',
					`/verifier/${produitId}?quantite=${quantite}`,
				);

				if (response?.success) {
					setStockStatus({
						suffisant: response.data?.suffisant,
						stockDisponibleTotal: response.data?.stockDisponibleTotal || 0,
						quantiteDemandee: quantite,
					});
				} else {
					setStockStatus({
						suffisant: false,
						error: response?.error,
						quantiteDemandee: quantite,
					});
				}
			} catch (error) {
				setStockStatus({
					suffisant: false,
					error: error.message,
					quantiteDemandee: quantite,
				});
			} finally {
				setCheckingStock(false);
			}
		},
		[ligne.type],
	);

	useEffect(() => {
		if (ligne?.type === 'produit' && ligne?.produitId && ligne?.quantite) {
			const productId =
				typeof ligne.produitId === 'string' ? ligne.produitId : ligne.produitId._id;
			const quantite = parseFloat(ligne.quantite) || 0;
			if (quantite > 0) checkStock(productId, quantite);
		} else {
			setStockStatus(null);
		}
	}, [ligne?.produitId, ligne?.quantite, ligne?.type, checkStock]);

	useEffect(() => {
		if (ligne?.produitId && ligne.type === 'produit') {
			const productId =
				typeof ligne.produitId === 'string' ? ligne.produitId : ligne.produitId._id;
			const product = products.find((p) => p._id === productId);
			setSelectedProduct(product);
			if (product && !ligne.nom) {
				onUpdate(index, 'description', product.description || '');
			}
		} else {
			setSelectedProduct(null);
		}
	}, [ligne?.produitId, products, ligne?.type, ligne?.nom, index, onUpdate]);

	const handleProductChange = useCallback(
		(product) => {
			if (!product) {
				onUpdate(index, 'produitId', '');
				onUpdate(index, 'nom', '');
				onUpdate(index, 'prixUnitaireHT', 0);
				setSelectedProduct(null);
				setStockStatus(null);
				return;
			}

			setSelectedProduct(product);
			onUpdate(index, 'produitId', product._id);
			onUpdate(index, 'description', product.description || '');
			onUpdate(index, 'prixUnitaireHT', product.sellingPrice || 0);

			const quantite = parseFloat(ligne.quantite) || 1;
			if (quantite > 0) {
				checkStock(product._id, quantite);
			}
		},
		[index, onUpdate, ligne.quantite, checkStock],
	);

	const handleProductCreated = useCallback(
		(newProduct) => {
			console.log('🔥 handleProductCreated appelé avec:', newProduct);
			console.log('Valeur de selectedProduct avant:', selectedProduct);
			console.log('Valeur de ligne avant:', ligne);

			if (newProduct) {
				// Force la mise à jour de selectedProduct
				setSelectedProduct(newProduct);

				// Puis appelle handleProductChange
				setTimeout(() => {
					handleProductChange(newProduct);
				}, 0);
			}
		},
		[handleProductChange, selectedProduct, ligne],
	);

	const handleQuantiteChange = useCallback(
		(value) => {
			const quantite = parseFloat(value) || 0;
			onUpdate(index, 'quantite', quantite);
			if (ligne.type === 'produit' && ligne.produitId && quantite > 0) {
				const productId =
					typeof ligne.produitId === 'string' ? ligne.produitId : ligne.produitId._id;
				checkStock(productId, quantite);
			}
		},
		[index, onUpdate, ligne.type, ligne.produitId, checkStock],
	);

	const handleTypeChange = useCallback(
		(value) => {
			onUpdate(index, 'type', value);
			setStockStatus(null);
			setSelectedProduct(null);
			if (value !== 'produit') {
				onUpdate(index, 'produitId', '');
			}
		},
		[index, onUpdate],
	);

	const totalLigneHT = useMemo(() => {
		return (parseFloat(ligne?.quantite) || 0) * (parseFloat(ligne?.prixUnitaireHT) || 0);
	}, [ligne?.quantite, ligne?.prixUnitaireHT]);

	const StockIndicator = useMemo(() => {
		if (ligne.type !== 'produit') return null;

		if (checkingStock) {
			return <Spinner size='sm' className='ms-1' />;
		}

		if (!stockStatus || stockStatus.error) {
			return <Icon icon='Help' size='sm' className='ms-1 text-muted' />;
		}

		if (stockStatus.suffisant) {
			return <Icon icon='Check' size='sm' className='ms-1 text-success' />;
		} else {
			return (
				<div className='d-flex align-items-center ms-1'>
					<Icon icon='Cancel' size='sm' className='text-danger' />
					<small className='text-danger ms-1'>{stockStatus.stockDisponibleTotal}</small>
				</div>
			);
		}
	}, [ligne.type, checkingStock, stockStatus]);

	const renderProductOption = useCallback(
		(product) => {
			if (!product) return null;

			return (
				<div className='d-flex align-items-center justify-content-between w-100'>
					<div className='d-flex flex-column'>
						<span className='fw-medium'>{product.name || 'Sans nom'}</span>
						<small className='text-muted'>
							Stock: {product.inventory?.currentStock || 0} {product.unit || 'unité'}
							{product.sellingPrice &&
								` • ${formatCurrency(product.sellingPrice, company?.currency)}`}
						</small>
					</div>
					<div>
						{product.inventory?.currentStock <= (product.inventory?.minStock || 0) && (
							<Badge color='warning' className='me-1'>
								Stock bas
							</Badge>
						)}
						{!product.active && <Badge color='secondary'>Inactif</Badge>}
					</div>
				</div>
			);
		},
		[company?.currency],
	);

	// Mobile/Tablet Card View
	const renderMobileCard = () => {
		if (ligne.type == 'note') {
			return (
				<Card className='mb-3 border border-gray-300'>
					<CardBody>
						<div className='col-12'>
							<label className='form-label  text-muted mb-1'>Note</label>
							<Textarea
								rows={8}
								value={ligne?.description || ''}
								onChange={(e) => onUpdate(index, 'description', e.target.value)}
								disabled={loading}
								placeholder='Description'
								size='sm'
							/>
						</div>
						<Button
							color='danger'
							// isOutline
							isLight
							onClick={() => onRemove(index)}
							disabled={loading}
							size='sm'
							className='w-100 mt-2'>
							<Icon icon='Delete' size='sm' /> Supprimer
						</Button>
					</CardBody>
				</Card>
			);
		} else {
			return (
				<Card className='mb-3 border border-gray-300'>
					<CardBody>
						<div className='row g-2 mb-2'>
							{/* <div className='col-12'>
								<label className='form-label  text-muted mb-1'>Type</label>
								<Select
									value={ligne.type || 'produit'}
									onChange={(e) => handleTypeChange(e.target.value)}
									disabled={loading}
									size='sm'>
									{typeLigneOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</Select>
							</div> */}

							{ligne.type === 'produit' && (
								<div className='col-12'>
									<label className='form-label  text-muted mb-1'>Produit</label>
									<div className='d-flex align-items-center gap-1'>
										<div style={{ flex: 1 }}>
											<SearchableDropdown
												value={selectedProduct}
												onChange={handleProductChange}
												fetchOptions={searchProduits}
												placeholder='Rechercher un produit...'
												disabled={loading}
												idField='_id'
												displayField='name'
												subField='supplierReference'
												multiple={false}
												clearable={true}
												size='sm'
												icon='Search'
												variant='default'
												renderOption={renderProductOption}
												showAddButton={true}
												addButtonIcon='Add'
												modalComponent={CreationModal}
												modalProps={{
													title: 'Créer un nouveau produit',
													getDynamicFields: getProduitCreationFields,
													submit: createProduit,
													size: 'lg',
													submitLabel: 'Créer le produit',
													onSuccess: handleProductCreated,
													refreshOnSuccess: true,
												}}
												noResultsMessage='Aucun produit trouvé'
												emptyMessage='Commencez à taper pour rechercher un produit'
												loadingMessage='Recherche des produits...'
												className='mb-0'
												inputClassName='py-1'
											/>
										</div>
										{StockIndicator}
									</div>
								</div>
							)}

							{/* <div className='col-12'>
								<label className='form-label  text-muted mb-1'>Nom</label>
								<Input
									type='text'
									value={ligne?.nom || ''}
									onChange={(e) => onUpdate(index, 'nom', e.target.value)}
									disabled={
										loading || (ligne.type === 'produit' && selectedProduct)
									}
									size='sm'
									placeholder='Nom'
								/>
							</div> */}

							<div className='col-12'>
								<label className='form-label  text-muted mb-1'>Description</label>
								<Textarea
									rows={8}
									value={ligne?.description || ''}
									onChange={(e) => onUpdate(index, 'description', e.target.value)}
									disabled={loading}
									placeholder='Description'
									size='sm'
								/>
							</div>

							<div className='col-6'>
								<label className='form-label  text-muted mb-1'>Quantité</label>
								<Input
									type='number'
									step='0.001'
									min='0.001'
									value={ligne?.quantite || 1}
									onChange={(e) =>
										handleQuantiteChange(parseFloat(e.target.value))
									}
									disabled={loading || ligne?.type === 'note'}
									size='sm'
								/>
							</div>

							<div className='col-6'>
								<label className='form-label  text-muted mb-1'>Prix</label>
								<Input
									type='number'
									step='0.01'
									min='0'
									value={ligne?.prixUnitaireHT || 0}
									onChange={(e) =>
										onUpdate(
											index,
											'prixUnitaireHT',
											parseFloat(e.target.value),
										)
									}
									disabled={loading || ligne?.type === 'note'}
									size='sm'
								/>
							</div>
						</div>

						<div className='border-top pt-2 mt-2'>
							<div className='d-flex justify-content-between align-items-center'>
								<span className=' text-muted'>Total:</span>
								<span className='fw-bold'>
									{formatCurrency(totalLigneHT, company?.currency)}
								</span>
							</div>
						</div>
						<Button
							color='danger'
							// isOutline
							isLight
							onClick={() => onRemove(index)}
							disabled={loading}
							size='sm'
							className='w-100 mt-2'>
							<Icon icon='Delete' size='sm' /> Supprimer
						</Button>
					</CardBody>
				</Card>
			);
		}
	};
	// Desktop Table Row View
	const renderDesktopRow = () => (
		<tr>
			{/* Colonne Produit/Type - 20% */}
			{/*<td style={{ width: '20%', minWidth: '250px' }}>
				 <div className='d-flex flex-column gap-2'>
					{ligne?.type === 'produit' ? (
						<div className='d-flex align-items-center gap-1 w-100'>
							<div style={{ flex: 1 }}>
								<SearchableDropdown
									value={selectedProduct}
									onChange={handleProductChange}
									fetchOptions={searchProduits}
									placeholder='Rechercher un produit...'
									disabled={loading}
									idField='_id'
									displayField='name'
									subField='supplierReference'
									multiple={false}
									clearable={true}
									size='sm'
									icon='Search'
									variant='default'
									renderOption={renderProductOption}
									showAddButton={true}
									addButtonIcon='Add'
									modalComponent={CreationModal}
									modalProps={{
										title: 'Créer un nouveau produit',
										getDynamicFields: getProduitCreationFields,
										submit: createProduit,
										size: 'lg',
										submitLabel: 'Créer le produit',
									}}
									noResultsMessage='Aucun produit trouvé'
									emptyMessage='Commencez à taper pour rechercher un produit'
									loadingMessage='Recherche des produits...'
									className='mb-0'
									inputClassName='py-1'
								/>
							</div>
							{StockIndicator}
						</div>
					) : (
						<div className='text-muted '>
							{ligne.type === 'service' ? 'Service' : 'Note'}
						</div>
					)}
				</div> 
			</td>
*/}
			{/* Colonne Nom - 15% */}
			{/* <td style={{ width: '15%', minWidth: '150px' }}>
				<Input
					type='text'
					value={ligne?.nom || ''}
					onChange={(e) => onUpdate(index, 'nom', e.target.value)}
					disabled={loading || (ligne.type === 'produit' && selectedProduct)}
					size='sm'
					placeholder='Nom'
					className='w-100'
				/>
			</td> */}

			{/* Colonne Description - 25% */}
			<td style={{ width: '60%', }}>
				<Textarea
					rows={ligne?.type == 'note' ? 1 : 8}
					value={ligne?.description || ''}
					onChange={(e) => onUpdate(index, 'description', e.target.value)}
					disabled={loading}
					placeholder='Description'
					className='w-100'
					style={{ resize: 'vertical', minHeight: '75px' }}
				/>
			</td>
			{/* Colonne Quantité - 10% */}
			{ligne?.type !== 'note' ?
				<>

					<td style={{ width: '10%', minWidth: '100px' }}>
						<Input
							type='number'
							step='0.001'
							min='0.001'
							value={ligne?.quantite || 1}
							onChange={(e) => handleQuantiteChange(parseFloat(e.target.value))}
							disabled={loading || ligne?.type === 'note'}
							size='sm'
							className='w-100'
							onWheel={(e) => e.target.blur()}
						/>
					</td>



					{/* Colonne Prix unitaire HT - 15% */}
					<td style={{ width: '15%', minWidth: '120px' }}>
						<Input
							type='number'
							step='0.01'
							min='0'
							value={ligne?.prixUnitaireHT || 0}
							onChange={(e) => onUpdate(index, 'prixUnitaireHT', parseFloat(e.target.value))}
							disabled={loading || ligne?.type === 'note'}
							size='sm'
							className='w-100'
							onWheel={(e) => e.target.blur()}
						/>
					</td>

					{/* Colonne Total - 10% */}
					<td style={{ width: '10%', minWidth: '100px' }} className='text-end'>
						<div className='fw-bold'>{formatCurrency(totalLigneHT, company?.currency)}</div>
					</td>
				</> : <>
					<td>

					</td>
					<td></td>
					<td></td>
				</>
			}
			{/* Colonne Actions - 5% */}
			<td style={{ width: '5%', minWidth: '60px' }} className='text-center'>
				<Button
					color='danger'
					isOutline
					onClick={() => onRemove(index)}
					disabled={loading}
					size='sm'
					className='px-2'>
					<Icon icon='Delete' size='sm' />
				</Button>
			</td>
		</tr >
	);

	// Return appropriate view based on device detection
	if (shouldRenderMobileCard) {
		return renderMobileCard();
	}

	return renderDesktopRow();
};

export default React.memo(DevisLigneRow);
